import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { type User, type UserType, getUserById, getUsers } from "./userAPI";
import type { AxiosError } from "axios";
import { type HseSummaryData, getHseSummary } from "./hseAPI";
import { type AnalyticsReport, getAnalyticsReport } from "./analyticsAPI";
import {
    type DashboardWidgetProvisioning,
    type ProductInstance,
    getDashboardWidgets,
    getSubscribedProductsByCustomerId,
} from "./productAPI";
import { formatDateYMD } from "@dashboard/utils/formatDate";
import type { DashboardProfile, DashboardProfileMetadata } from "@dashboard/utils/dashboard.types";
import {
    type AnnouncementListResponse,
    type AnnouncementQuery,
    type ListsWithTasksQuery,
    type Task,
    type TaskAccess,
    type TaskAbilityResponse,
    type TaskList,
    type TaskListResponse,
    type TaskListWithTaskOccurrences,
    type TaskOccurrenceListResponse,
    type TaskOccurrenceQuery,
    type TaskQuery,
    getAnnouncements,
    getAnnouncementReactions,
    getListsWithTasks,
    getTaskAccess,
    getTaskAbility,
    getTaskById,
    getTaskListById,
    getTaskListWithTasks,
    getTaskOccurrences,
    getTasks,
} from "./task";
import {
    createDashboardProfile,
    getDashboardProfile,
    getDashboardProfiles,
} from "./dashboardProfileAPI";
import {
    createDefaultDashboardProfileConfig,
    getLegacyDashboardProfileConfig,
    removeLegacyDashboardProfileConfig,
} from "@dashboard/utils/dashboardProfileConfig";

const userKeys = {
    all: ["user"] as const,
};

function userByIdOptions({ id }: { id: number | null }) {
    return queryOptions<User, AxiosError>({
        queryKey: [...userKeys.all, id],
        queryFn: async () => (id ? await getUserById(id) : ({} as User)),
        enabled: !!id,
    });
}

interface UsersInfiniteParams {
    search?: string;
    customerId?: number;
    pageSize?: number;
    type?: UserType[];
}

function usersInfiniteOptions(params: UsersInfiniteParams, enabled: boolean = true) {
    const { search = "", customerId, pageSize = 20, type } = params;
    return infiniteQueryOptions({
        queryKey: ["usersInfinite", { search, customerId, pageSize, type }],
        queryFn: async ({ pageParam = 0 }) => {
            const encodedSearch = search ? encodeURIComponent(search) : "";
            const result = await getUsers({
                skip: pageParam,
                take: pageSize,
                search: encodedSearch,
                customerId: customerId ? [customerId] : [],
                type,
                orderKey: "fullName",
                order: "asc",
                deleted: false,
            });
            return result;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const totalFetched = allPages.reduce((acc, page) => acc + page.data.length, 0);
            if (totalFetched >= lastPage.total) {
                return undefined;
            }
            return totalFetched;
        },
        enabled,
    });
}

function hseSummaryOptions(customerId?: number) {
    return queryOptions<HseSummaryData>({
        queryKey: ["hseSummary", { customerId }],
        queryFn: getHseSummary,
    });
}

function taskAccessOptions({ userId, customerId }: { userId: number | null; customerId?: number }) {
    return queryOptions<TaskAccess[], Error | AxiosError>({
        queryKey: ["taskAccess", { userId, customerId }],
        queryFn: async () => (userId ? await getTaskAccess({ userId, customerId }) : []),
        enabled: !!userId,
    });
}

function taskAbilityOptions(enabled: boolean = true) {
    return queryOptions<TaskAbilityResponse, Error | AxiosError>({
        queryKey: ["taskAbility"],
        queryFn: async () => await getTaskAbility(),
        enabled,
    });
}

function tasksOptions(queries: TaskQuery = {}, enabled: boolean = true) {
    return queryOptions<TaskListResponse, AxiosError>({
        queryKey: ["tasks", queries],
        queryFn: () => getTasks(queries),
        enabled,
    });
}

const annualCycleTasksPageSize = 100;

// Annual cycle views search, filter, sort and paginate client-side, so they need every task
function annualCycleTasksOptions(
    queries: Omit<TaskQuery, "skip" | "take" | "annualCycle"> = {},
    enabled = true,
) {
    return queryOptions<Task[], AxiosError>({
        queryKey: ["tasks", "annualCycle", queries],
        queryFn: async ({ signal }) => {
            const fetchPage = (skip: number) =>
                getTasks(
                    { ...queries, annualCycle: true, skip, take: annualCycleTasksPageSize },
                    { signal },
                );

            const first = await fetchPage(0);
            const pageCount = Math.ceil(first.total / annualCycleTasksPageSize);
            const rest = await Promise.all(
                Array.from({ length: pageCount - 1 }, (_, i) =>
                    fetchPage((i + 1) * annualCycleTasksPageSize),
                ),
            );

            // Offset pages can overlap if a task is created between requests
            const tasks = new Map<number, Task>();
            for (const task of [first, ...rest].flatMap((page) => page.data)) {
                tasks.set(task.id, task);
            }
            return [...tasks.values()];
        },
        enabled,
    });
}

function taskOptions(id: number | null) {
    return queryOptions<Task, AxiosError>({
        queryKey: ["task", id],
        queryFn: async () => (id ? await getTaskById(id) : ({} as Task)),
        enabled: !!id,
    });
}

function taskListOptions(id: number) {
    return queryOptions<TaskList, AxiosError>({
        queryKey: ["taskList", id],
        queryFn: async () => (id ? await getTaskListById(id) : ({} as TaskList)),
        enabled: !!id,
    });
}

const taskListWithTasksKeys = {
    all: ["taskListWithTasks"] as const,
};

function taskListWithTasksOptions(id: number) {
    return queryOptions<TaskListWithTaskOccurrences, AxiosError>({
        queryKey: [...taskListWithTasksKeys.all, id],
        queryFn: () => getTaskListWithTasks(id),
        enabled: !!id,
    });
}

function listsWithTasksOptions(queries: ListsWithTasksQuery = {}, enabled: boolean = true) {
    return queryOptions<TaskListWithTaskOccurrences[], AxiosError>({
        queryKey: ["listsWithTasks", queries],
        queryFn: () => getListsWithTasks(queries),
        enabled,
    });
}

function taskOccurrencesOptions(queries: TaskOccurrenceQuery = {}, enabled: boolean = true) {
    return queryOptions<TaskOccurrenceListResponse, AxiosError>({
        queryKey: ["taskOccurrences", queries],
        queryFn: () => getTaskOccurrences(queries),
        enabled,
    });
}

function analyticsReportOptions(customerId: number | null, startDate: Date, endDate: Date) {
    return queryOptions<AnalyticsReport, AxiosError>({
        queryKey: ["analyticsReport", customerId, formatDateYMD(startDate), formatDateYMD(endDate)],
        queryFn: async () => getAnalyticsReport(customerId!, startDate, endDate),
        enabled: !!customerId,
        staleTime: 1000 * 60 * 60 * 24,
        gcTime: 1000 * 60 * 60 * 48,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        placeholderData: keepPreviousData,
    });
}

function productInstancesByCustomerOptions(customerId: number | null) {
    return queryOptions<ProductInstance[], AxiosError>({
        queryKey: ["productInstances", customerId],
        queryFn: async () => getSubscribedProductsByCustomerId(customerId!),
        enabled: !!customerId,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60 * 24,
        placeholderData: keepPreviousData,
    });
}

function dashboardWidgetsOptions(productInstanceId: number | null) {
    return queryOptions<DashboardWidgetProvisioning[], AxiosError>({
        queryKey: ["dashboardWidgets", productInstanceId],
        queryFn: async () => getDashboardWidgets(productInstanceId!),
        enabled: !!productInstanceId,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
    });
}

const dashboardProfilesQueryKey = (productInstanceId: number) =>
    ["dashboardProfiles", productInstanceId] as const;

function dashboardProfilesOptions(
    productInstanceId: number,
    getInitialProfileName: () => string,
    onProfileCreated: (profile: DashboardProfile) => void,
) {
    return queryOptions<DashboardProfileMetadata[], AxiosError>({
        queryKey: dashboardProfilesQueryKey(productInstanceId),
        queryFn: async () => {
            const profiles = await getDashboardProfiles(productInstanceId);
            if (profiles.length > 0) return profiles;

            const config =
                getLegacyDashboardProfileConfig() ?? createDefaultDashboardProfileConfig();
            const createdProfile = await createDashboardProfile(productInstanceId, {
                name: getInitialProfileName(),
                config,
            });

            onProfileCreated(createdProfile);
            removeLegacyDashboardProfileConfig();

            return [
                {
                    id: createdProfile.id,
                    name: createdProfile.name,
                    createdAt: createdProfile.createdAt,
                    updatedAt: createdProfile.updatedAt,
                },
            ];
        },
        retry: false,
    });
}

function dashboardProfileOptions(productInstanceId: number, profileId: number | null) {
    return queryOptions<DashboardProfile, AxiosError>({
        queryKey: ["dashboardProfile", productInstanceId, profileId],
        queryFn: async () => {
            if (profileId === null) {
                throw new Error("A dashboard profile ID is required.");
            }
            return getDashboardProfile(productInstanceId, profileId);
        },
        enabled: profileId !== null,
    });
}

function announcementsOptions(query: AnnouncementQuery) {
    return queryOptions<AnnouncementListResponse, AxiosError>({
        queryKey: ["announcements", query],
        queryFn: () => getAnnouncements(query),
        enabled: !!query.instanceId,
    });
}

function announcementReactionsOptions(id: number) {
    return queryOptions({
        queryKey: ["announcementReactions", id],
        queryFn: () => getAnnouncementReactions(id),
    });
}

export {
    analyticsReportOptions,
    announcementsOptions,
    announcementReactionsOptions,
    dashboardWidgetsOptions,
    dashboardProfilesOptions,
    dashboardProfilesQueryKey,
    dashboardProfileOptions,
    hseSummaryOptions,
    listsWithTasksOptions,
    taskAccessOptions,
    taskAbilityOptions,
    taskListOptions,
    taskListWithTasksKeys,
    taskListWithTasksOptions,
    taskOccurrencesOptions,
    taskOptions,
    tasksOptions,
    annualCycleTasksOptions,
    productInstancesByCustomerOptions,
    userByIdOptions,
    userKeys,
    usersInfiniteOptions,
};
